import { inject } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";

import { Observable, throwError } from "rxjs";
import { TranslateService } from "@ngx-translate/core";

import { ECodeError, IError } from "../interfaces/error.interface";

/**
 * Abstract class to be used as a base for other services, providing generic information and facilitating development and organization.
 */
export abstract class BaseService {
	/** TranslateService instance for internationalization */
	private translateService = inject(TranslateService);

	/**
	 * Generic method to handle errors in services.
	 * @param {(HttpErrorResponse | object)} error - The error object received from an HTTP request.
	 * @returns {Observable<never>} - Observable that throws an error with a user-friendly message.
	 */
	protected serviceError(error: HttpErrorResponse | object): Observable<never> {
		if (error instanceof HttpErrorResponse && error.status === 410) {
			return throwError(() => error);
		}

		let errorMessage: string;

		if (error instanceof HttpErrorResponse) {
			if (error.status === 403) {
				errorMessage = "NOTIFICATION.YOU_DO_NOT_HAVE_PERMISSION";
			} else {
				errorMessage = this.getHttpErrorMessage(error);
			}
		}

		// Check if the error message contains "invalid refresh token"
		// eslint-disable-next-line @typescript-eslint/no-base-to-string
		if (error.toString().includes("invalid refresh token")) {
			errorMessage = "NOTIFICATION.SESSION_EXPIRED";
		}

		return throwError(() => errorMessage ? new Error(errorMessage) : error);
	}

	private getHttpErrorMessage(error: HttpErrorResponse): string {
		const err: IError = error.error as IError;
		const fieldErrorCodes = new Set([
			ECodeError.MANDATORY_FIELD,
			ECodeError.DUPLICATED_FIELD,
			ECodeError.FIELD_TYPE,
			ECodeError.DOCUMENT_INVALID,
			ECodeError.REGISTER_DUPLICATED,
			ECodeError.USER_WITHOUT_EMAIL,
			ECodeError.CUSTOMER_NOT_FOUND,
			ECodeError.REQUIREMENT_NOT_FOUND,
			ECodeError.ACTION_CONCLUDED_FUTURE_DATE,
			ECodeError.PIPELINE_NAME_DUPLICATED,
			ECodeError.OPPORTUNITY_CLOSING_DATE_BEFORE_NOW,
			ECodeError.PIPELINE_LINKED_OPPORTUNITIES,
			ECodeError.PIPELINE_HAS_ACTIVE_OPPORTUNITIES,
			ECodeError.OPPORTUNITY_STAGE_CONFLICT,
			ECodeError.OPPORTUNITY_CONCLUDED_FIELD_LOCKED,
			ECodeError.PIPELINE_INACTIVE,
			ECodeError.OPPORTUNITY_PIPELINE_MIGRATION_SAME_PIPELINE,
			ECodeError.OPPORTUNITY_PIPELINE_MIGRATION_RESPONSIBLE_ONLY,
			ECodeError.COMMERCIAL_ACTION_OPPORTUNITY_LINK_INVALID,
			ECodeError.OPPORTUNITY_INCOTERM_INVALID,
			ECodeError.OPPORTUNITY_VALIDITY_END_DATE_INVALID,
			ECodeError.OPPORTUNITY_BID_ROUND_COUNT_INVALID,
			ECodeError.OPPORTUNITY_BID_CURRENT_ROUND_INVALID,
			ECodeError.OPPORTUNITY_BID_ROUND_DEADLINE_INVALID,
			ECodeError.OPPORTUNITY_DANGEROUS_CARGO_UN_INVALID,
			ECodeError.OPPORTUNITY_REFRIGERATED_CARGO_SHELF_LIFE_INVALID,
			ECodeError.GRID_FILTER_FIELD_NOT_ALLOWED,
			ECodeError.GRID_FILTER_NUMBER_VALUE_INVALID,
			ECodeError.GRID_FILTER_CONDITION_NOT_ALLOWED,
			ECodeError.OPPORTUNITY_VOLUME_TYPE_LOCKED,
		]);

		if (fieldErrorCodes.has(err.code)) {
			return this.getErrorMessage(err.code, err.message.property);
		}

		let message = error.message;
		if (error.error && typeof error.error.message === "string") {
			message = error.error.message as string;
		}

		return `Server returned code: ${error.status}, error message is: ${message}`;
	}

	/**
	 * Get the translated error message based on the error code.
	 * @param {ECodeError} errorCode - The error code to translate.
	 * @param {string} property - The property associated with the error.
	 * @returns {string} - The translated error message.
	 */
	private getErrorMessage(errorCode: ECodeError, property: string): string {
		const errorMessages = {
			[ECodeError.MANDATORY_FIELD]: "NOTIFICATION.MANDATORY_FIELD",
			[ECodeError.DUPLICATED_FIELD]: "NOTIFICATION.DUPLICATED_FIELD",
			[ECodeError.FIELD_TYPE]: "NOTIFICATION.FIELD_TYPE",
			[ECodeError.DOCUMENT_INVALID]: "NOTIFICATION.INVALID_FIELD",
			[ECodeError.REGISTER_DUPLICATED]: "NOTIFICATION.REGISTER_DUPLICATED",
			[ECodeError.USER_WITHOUT_EMAIL]: "NOTIFICATION.COMMERCIAL_ACTION_USER_WITHOUT_EMAIL",
			[ECodeError.USER_SYNC_TIME_LIMIT]: "NOTIFICATION.USER_SYNC_TIME_LIMIT",
			[ECodeError.CUSTOMER_NOT_FOUND]: "NOTIFICATION.CUSTOMER_NOT_FOUND",
			[ECodeError.REQUIREMENT_NOT_FOUND]: "NOTIFICATION.REQUIREMENT_NOT_FOUND",
			[ECodeError.SCHEDULING_FAILED]: "NOTIFICATION.SCHEDULING_FAILED",
			[ECodeError.ACTION_CONCLUDED_FUTURE_DATE]: "NOTIFICATION.ACTION_CONCLUDED_FUTURE_DATE",
			[ECodeError.PIPELINE_NAME_DUPLICATED]: "NOTIFICATION.PIPELINE_NAME_DUPLICATED",
			[ECodeError.OPPORTUNITY_CLOSING_DATE_BEFORE_NOW]: "NOTIFICATION.OPPORTUNITY_CLOSING_DATE_BEFORE_NOW",
			[ECodeError.PIPELINE_LINKED_OPPORTUNITIES]: "NOTIFICATION.PIPELINE_LINKED_OPPORTUNITIES",
			[ECodeError.PIPELINE_HAS_ACTIVE_OPPORTUNITIES]: "NOTIFICATION.PIPELINE_HAS_ACTIVE_OPPORTUNITIES",
			[ECodeError.OPPORTUNITY_STAGE_CONFLICT]: "NOTIFICATION.OPPORTUNITY_STAGE_CONFLICT",
			[ECodeError.OPPORTUNITY_CONCLUDED_FIELD_LOCKED]: "NOTIFICATION.OPPORTUNITY_CONCLUDED_FIELD_LOCKED",
			[ECodeError.PIPELINE_INACTIVE]: "NOTIFICATION.PIPELINE_INACTIVE",
			[ECodeError.OPPORTUNITY_PIPELINE_MIGRATION_SAME_PIPELINE]: "NOTIFICATION.OPPORTUNITY_PIPELINE_MIGRATION_SAME_PIPELINE",
			[ECodeError.OPPORTUNITY_PIPELINE_MIGRATION_RESPONSIBLE_ONLY]: "OPPORTUNITY_PIPELINE_MIGRATION_RESPONSIBLE_ONLY",
			[ECodeError.COMMERCIAL_ACTION_OPPORTUNITY_LINK_INVALID]: "NOTIFICATION.ACTION_OPPORTUNITY_LINK_INVALID",
			[ECodeError.OPPORTUNITY_INCOTERM_INVALID]: "NOTIFICATION.OPPORTUNITY_INCOTERM_INVALID",
			[ECodeError.OPPORTUNITY_VALIDITY_END_DATE_INVALID]: "NOTIFICATION.OPPORTUNITY_VALIDITY_END_DATE_INVALID",
			[ECodeError.OPPORTUNITY_BID_ROUND_COUNT_INVALID]: "NOTIFICATION.OPPORTUNITY_BID_ROUND_COUNT_INVALID",
			[ECodeError.OPPORTUNITY_BID_CURRENT_ROUND_INVALID]: "NOTIFICATION.OPPORTUNITY_BID_CURRENT_ROUND_INVALID",
			[ECodeError.OPPORTUNITY_BID_ROUND_DEADLINE_INVALID]: "NOTIFICATION.OPPORTUNITY_BID_ROUND_DEADLINE_INVALID",
			[ECodeError.OPPORTUNITY_DANGEROUS_CARGO_UN_INVALID]: "NOTIFICATION.OPPORTUNITY_DANGEROUS_CARGO_UN_INVALID",
			[ECodeError.OPPORTUNITY_REFRIGERATED_CARGO_SHELF_LIFE_INVALID]: "NOTIFICATION.OPPORTUNITY_REFRIGERATED_CARGO_SHELF_LIFE_INVALID",
			[ECodeError.GRID_FILTER_FIELD_NOT_ALLOWED]: "NOTIFICATION.GRID_FILTER_FIELD_NOT_ALLOWED",
			[ECodeError.GRID_FILTER_NUMBER_VALUE_INVALID]: "NOTIFICATION.GRID_FILTER_NUMBER_VALUE_INVALID",
			[ECodeError.GRID_FILTER_CONDITION_NOT_ALLOWED]: "NOTIFICATION.GRID_FILTER_CONDITION_NOT_ALLOWED",
			[ECodeError.OPPORTUNITY_VOLUME_TYPE_LOCKED]: "OPPORTUNITY_VOLUME_TYPE_LOCKED",
		};

		return this.translateService.instant(errorMessages[errorCode], { field: property }) as string;
	}
}